import { useEffect, useRef, useState, useCallback } from 'react';

export interface PresenceUser {
  userId: string;
  userName: string;
  userInitial: string;
  cursor?: { x: number; y: number } | null;
}

const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_DELAY = 30000;

export function usePresence(canvasId: string, userId: string, userName: string): PresenceUser[] {
  const [users, setUsers] = useState<PresenceUser[]>([]);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);

  const connect = useCallback(() => {
    if (!canvasId || !userId) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/ws/canvas?canvasId=${encodeURIComponent(canvasId)}&userId=${encodeURIComponent(userId)}&userName=${encodeURIComponent(userName)}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'presence-list') {
          setUsers((data.payload.users || []).map((u: any) => ({ ...u, cursor: null })));
        } else if (data.type === 'presence-joined') {
          setUsers(prev => {
            if (prev.some(u => u.userId === data.payload.userId)) return prev;
            return [...prev, { ...data.payload, cursor: null }];
          });
        } else if (data.type === 'presence-left') {
          setUsers(prev => prev.filter(u => u.userId !== data.payload.userId));
        } else if (data.type === 'cursor-moved') {
          const { userId: uid, x, y } = data.payload;
          setUsers(prev => {
            const idx = prev.findIndex(u => u.userId === uid);
            if (idx === -1) return prev;
            const updated = [...prev];
            updated[idx] = {
              ...updated[idx],
              cursor: x != null && y != null ? { x, y } : null,
            };
            return updated;
          });
        }
      } catch {}
    };

    ws.onclose = () => {
      if (wsRef.current === ws) {
        const delay = Math.min(
          RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current),
          MAX_RECONNECT_DELAY
        );
        reconnectAttemptRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          if (wsRef.current === ws || wsRef.current === null) {
            connect();
          }
        }, delay);
      }
    };

    ws.onerror = () => {};
  }, [canvasId, userId, userName]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return users;
}
